/* eslint-disable camelcase  */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
// @mui
import {
  Autocomplete,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TablePagination,
  TextField,
  Typography
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ModernAgGrid from '../components/ModernAgGrid';
// components
import useAxiosWithAuth from '../api/useAxiosWithAuth';
import Iconify from '../components/Iconify';
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import useImportLocationsQuery from '../hooks/useImportLocationsQuery';

const ManageExports = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [filterValue, setFilterValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [tableData, setTableData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [selectedImportLocation, setSelectedImportLocation] = useState('');
  const [selectedPermitStatuses, setSelectedPermitStatuses] = useState(['Applied For', 'Unused', 'Sent to Exporter']);
  const [sortColumnName, setSortColumnName] = useState('');
  const [sortType, setSortType] = useState(''); // 'asc', 'desc', or ''
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  // Function to handle column header clicks for sorting
  const handleSort = (columnName) => {
    if (sortColumnName === columnName) {
      // Same column is clicked again
      if (sortType === 'asc') {
        // First click: asc -> desc
        setSortType('desc');
      } else if (sortType === 'desc') {
        // Second click: desc -> none (reset)
        setSortColumnName('');
        setSortType('');
      }
    } else {
      // Different column is clicked - start fresh with asc
      setSortColumnName(columnName);
      setSortType('asc');
    }
    // Reset to first page when sorting changes
    setPage(0);
  };

  // Helper function to get sort indicator
  const getSortIndicator = (field) => {
    if (sortColumnName === field) {
      if (sortType === 'asc') {
        return '↓'; // Down arrow for ascending
      }
      if (sortType === 'desc') {
        return '↑'; // Up arrow for descending
      }
    }
    return '↕'; // Default icon for unsorted columns
  };

  const [addExportDialogOpen, setAddExportDialogOpen] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [newExport, setNewExport] = useState({
    status: 'Applied For',
    product: '',
    qtyApplied: '',
    importLocation: '',
    applicationSubmitted: null,
    importPermit: '',
    originalPermitNo: '',
    file: null,
    permitExpiry: null,
  });
  const [newExportTempInput, setNewExportTempInput] = useState('');
  const [newExportErrors, setNewExportErrors] = useState({});
  const [updateExportDialogOpen, setUpdateExportDialogOpen] = useState(false);
  const [exportToUpdate, setExportToUpdate] = useState(null);
  const [updateExport, setUpdateExport] = useState({
    status: '',
    product: '',
    qtyApplied: '',
    importLocation: '',
    applicationSubmitted: null,
    importPermit: '',
    originalPermitNo: '',
    file: null,
    permitExpiry: null,
  });
  const [updateExportTempInput, setUpdateExportTempInput] = useState('');
  const [updateExportErrors, setUpdateExportErrors] = useState({});
  const axiosInstance = useAxiosWithAuth();
  const gridRef = useRef();
  const { locations: importLocations, loading: importLocationsLoading } = useImportLocationsQuery();


  // Debounce function - waits for a pause in input before calling the function
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  // Convert a date object to the required format with milliseconds (2025-09-11T10:36:11.000+00:00)
  const dateToCustomFormat = (date) => {
    if (!date) return null;
    // Create a new date object to ensure we're working with a valid date
    const dateObj = new Date(date);
    // Format as ISO string with milliseconds and replace Z with +00:00
    const isoString = dateObj.toISOString();
    // Replace the 'T' and 'Z' to get the required format with '.000' for milliseconds
    // The ISO string already has milliseconds, so we just replace Z
    return isoString.replace('Z', '+00:00');
  };

  // Convert an RFC3339 string to Date object
  const rfc3339ToDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString);
  };

  // Debounced product search function - waits for a pause in typing before calling the API
  const [debouncedProductSearch, setDebouncedFunction] = useState(null);

  // Initialize the debounced function when component mounts
  useEffect(() => {
    const searchFunction = async (searchTerm) => {
      if (searchTerm && searchTerm.length > 2) {
        // Only search if at least 3 characters
        try {
          const response = await axiosInstance.get(`/product/search/${searchTerm}`);
          if (response.status === 200 && response.data.product_code) {
            setProductOptions(response.data.product_code);
          } else {
            setProductOptions([]);
          }
        } catch (error) {
          console.error('Error searching products:', error);
          setProductOptions([]);
        }
      } else {
        setProductOptions([]);
      }
    };

    // Create debounced version of the function
    const debouncedSearch = debounce(searchFunction, 500);
    setDebouncedFunction(() => debouncedSearch);
  }, []);


  const formatDateForDisplay = (date) => {
    if (!date) return '';

    // Check if the date string is the default/minimal date from API
    if (date === '0001-01-01T00:00:00Z') {
      return 'N/A';
    }

    let dateObj;

    if (typeof date === 'string') {
      // Check if it's an RFC3339 formatted string or the custom format with milliseconds
      if (date.includes('T') && (date.includes('Z') || date.includes('+'))) {
        dateObj = new Date(date);
      } else {
        dateObj = new Date(date);
      }
    } else if (typeof date === 'number') {
      // If it's a Unix timestamp
      dateObj = new Date(date * 1000);
    } else {
      // If it's already a Date object
      dateObj = date;
    }

    // Format as DD/MM/YYYY
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const year = dateObj.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exportToDelete, setExportToDelete] = useState(null);
  const [total, setTotal] = useState(0);
  const [addFieldDialogOpen, setAddFieldDialogOpen] = useState(false);
  const [exportToAddField, setExportToAddField] = useState(null);
  const [addFieldData, setAddFieldData] = useState({
    actual_quantity: '',
    used_date: null,
  });
  const [addFieldErrors, setAddFieldErrors] = useState({});

  // Set up column definitions for AG-Grid
  useEffect(() => {
    setColumnDefs([
      {
        field: 'sl',
        headerName: '#SL',
        resizable: true,
        width: 80,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('sl')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('sl');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>#SL</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('sl')}</span>
          </div>
        ),
      },
      {
        field: 'permit_status',
        headerName: 'Status',
        resizable: true,
        width: 150,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('permit_status')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('permit_status');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Status</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('permit_status')}</span>
          </div>
        ),
      },
      {
        field: 'product',
        headerName: 'Product',
        resizable: true,
        width: 250,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('product')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('product');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Product</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('product')}</span>
          </div>
        ),
      },
      {
        field: 'qty_applied',
        headerName: 'Qty Applied',
        resizable: true,
        width: 120,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('qty_applied')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('qty_applied');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Qty Applied</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('qty_applied')}</span>
          </div>
        ),
      },
      {
        field: 'import_location',
        headerName: 'Import Location',
        resizable: true,
        width: 250,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('import_location')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('import_location');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Import Location</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('import_location')}</span>
          </div>
        ),
      },
      {
        field: 'application_submitted',
        headerName: 'Application Submitted',
        resizable: true,
        width: 200,
        valueFormatter: (params) => formatDateForDisplay(params.value),
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('application_submitted')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('application_submitted');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Application Submitted</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('application_submitted')}</span>
          </div>
        ),
      },
      {
        field: 'import_permit',
        headerName: 'Date Permit Received',
        resizable: true,
        width: 150,
        valueFormatter: (params) => formatDateForDisplay(params.value),
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('import_permit')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('import_permit');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Date Permit Received</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('import_permit')}</span>
          </div>
        ),
      },
      {
        field: 'original_permit_no',
        headerName: 'Permit Number',
        resizable: true,
        width: 150,
        cellRendererFramework: (params) =>
          params.data.file_name ? (
            <a href={params.data.file_name} target="_blank" rel="noopener noreferrer">
              {params.data.original_permit_no}
            </a>
          ) : (
            <span>{params.data.original_permit_no === 'undefined' ? '' : params.data.original_permit_no}</span>
          ),
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('original_permit_no')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('original_permit_no');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Permit Number</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('original_permit_no')}</span>
          </div>
        ),
      },
      {
        field: 'permit_expiry',
        headerName: 'Permit Expiry',
        resizable: true,
        width: 130,
        valueFormatter: (params) => formatDateForDisplay(params.value),
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('permit_expiry')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('permit_expiry');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Permit Expiry</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('permit_expiry')}</span>
          </div>
        ),
      },
      {
        field: 'actual_quantity',
        headerName: 'Actual Quantity',
        resizable: true,
        width: 130,
        valueFormatter: (params) => params.value || 'N/A',
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('actual_quantity')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('actual_quantity');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Actual Quantity</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('actual_quantity')}</span>
          </div>
        ),
      },
      {
        field: 'used_date',
        headerName: 'Used Date',
        resizable: true,
        width: 130,
        valueFormatter: (params) => (params.value ? formatDateForDisplay(params.value) : 'N/A'),
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('used_date')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('used_date');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Used Date</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('used_date')}</span>
          </div>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        cellRendererFramework: (params) => (
          <Stack direction="row" justifyContent="center" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              onClick={() => {
                handleUpdateClick(params.data);
              }}
            >
              Update
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="info"
              onClick={() => {
                setExportToAddField(params.data);
                setAddFieldDialogOpen(true);
                setAddFieldData({
                  actual_quantity: params.data.actual_quantity || '',
                  used_date: params.data.used_date ? rfc3339ToDate(params.data.used_date) : null,
                });
                setAddFieldErrors({});
              }}
            >
              Add Field
            </Button>
            {/*
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={() => {
                setExportToDelete(params.data);
                setDeleteDialogOpen(true);
              }}
            >
              Delete
            </Button>
            */}
          </Stack>
        ),
        width: 200,
        pinned: 'right',
      },
    ]);
  }, [tableData, sortColumnName, sortType]);

  const handleDeleteExport = async () => {
    try {
      const response = await axiosInstance.delete(`/trade/exports/${exportToDelete.id}`);
      if (response.status === 200) {
        toast.success(`Export "${exportToDelete.product}" deleted successfully`);

        // Refresh the export list to get the updated data from the server
        const refreshData = async () => {
          try {
            setIsLoading(true);
            // Calculate the statuses to ignore based on selected statuses
            const allStatuses = ['Applied For', 'Used', 'Unused', 'Sent to Exporter'];
            const statusesToIgnore = allStatuses.filter(status => !selectedPermitStatuses.includes(status));
            // If all statuses are selected, send [""], otherwise send the filtered array
            const finalIgnoreArray = statusesToIgnore.length === 0 ? [""] : statusesToIgnore;

            const response = await axiosInstance.post(
              `/trade/filter-exports?page_size=${rowsPerPage}&page_number=${page + 1}`,
              {
                search_key: filterValue,
                permit_status: '',
                import_location: selectedImportLocation, // Use the selected import location
                sort_column_name: sortColumnName, // Use the selected sort column
                sort_type: sortType, // Use the selected sort type
                ignore_permit_status: finalIgnoreArray,
              }
            );

            if (response.status === 200) {
              // The new response structure has meta and payload
              const { payload: data, meta } = response.data;
              setTotal(meta.total_size || data.length); // Set total for pagination from meta

              // Add serial numbers to the data
              const dataWithSerial = data.map((item, index) => ({
                ...item,
                sl: index + 1 + page * rowsPerPage, // Calculate serial based on current page
              }));

              setTableData(dataWithSerial);
            }
            setIsLoading(false);
          } catch (error) {
            setIsLoading(false);
            console.error('Error refreshing exports:', error);
          }
        };

        refreshData();
      }
    } catch (error) {
      console.error('Error deleting export:', error);
      if (error.response?.data?.message) {
        toast.error(`Failed to delete export: ${error.response.data.message}`);
      } else {
        toast.error('Failed to delete export. Please try again.');
      }
    }
    setDeleteDialogOpen(false);
    setExportToDelete(null);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setExportToDelete(null);
  };

  const handleUpdateClick = (exportData) => {
    setExportToUpdate(exportData);
    setUpdateExport({
      status: exportData.permit_status,
      product: exportData.product,
      qtyApplied: exportData.qty_applied,
      importLocation: exportData.import_location,
      applicationSubmitted:
        exportData.application_submitted !== '0001-01-01T00:00:00Z'
          ? rfc3339ToDate(exportData.application_submitted)
          : null,
      importPermit:
        exportData.import_permit !== '0001-01-01T00:00:00Z' ? rfc3339ToDate(exportData.import_permit) : null,
      originalPermitNo: exportData.original_permit_no,
      permitExpiry:
        exportData.permit_expiry !== '0001-01-01T00:00:00Z' ? rfc3339ToDate(exportData.permit_expiry) : null,
      file: null, // Reset file field for update
    });
    setUpdateExportErrors({});
    setUpdateExportDialogOpen(true);
  };

  const handleUpdateExportChange = (field, value) => {
    // Special validation for product field to ensure only valid options are accepted
    if (field === 'product') {
      // Check if the value exists in the options list
      const isValidOption = productOptions.includes(value);
      if (isValidOption || value === '') {
        // Only update if it's a valid option or empty
        setUpdateExport({
          ...updateExport,
          [field]: value,
        });
      } else {
        // If not valid, don't update the main state but keep the temp input value
        // This will cause the field to revert to the last valid value
      }
    } else {
      // For other fields, update normally
      setUpdateExport({
        ...updateExport,
        [field]: value,
      });
    }

    // Clear error when user updates the field
    if (updateExportErrors[field]) {
      setUpdateExportErrors({
        ...updateExportErrors,
        [field]: null,
      });
    }
  };

  const handleUpdateExportDialogClose = () => {
    setUpdateExportDialogOpen(false);
    setExportToUpdate(null);
    setUpdateExport({
      status: '',
      product: '',
      qtyApplied: '',
      importLocation: '',
      applicationSubmitted: null,
      importPermit: '',
      originalPermitNo: '',
      file: null,
      permitExpiry: null,
    });
    setUpdateExportErrors({});
  };

  const handleUpdateExport = async () => {
    // Basic validation
    const errors = {};
    if (!updateExport.status) errors.status = 'Status is required';
    if (!updateExport.product?.trim()) errors.product = 'Product is required';
    if (!updateExport.qtyApplied || updateExport.qtyApplied <= 0) errors.qtyApplied = 'Quantity must be greater than 0';
    if (!updateExport.importLocation) errors.importLocation = 'Import Location is required';
    if (!updateExport.applicationSubmitted) errors.applicationSubmitted = 'Application Submitted date is required';
    // if (!updateExport.importPermit) errors.importPermit = 'Import Permit is required';
    // if (!updateExport.originalPermitNo?.trim()) errors.originalPermitNo = 'Original Permit No. is required';
    // if (!updateExport.permitExpiry) errors.permitExpiry = 'Permit Expiry date is required';

    if (Object.keys(errors).length > 0) {
      setUpdateExportErrors(errors);
      return;
    }

    try {
      // Create FormData for the update
      const formData = new FormData();
      formData.append('permit_status', updateExport.status);
      formData.append('product', updateExport.product);
      formData.append('qty_applied', updateExport.qtyApplied);
      formData.append('import_location', updateExport.importLocation);
      formData.append(
        'application_submitted',
        updateExport.applicationSubmitted ? dateToCustomFormat(updateExport.applicationSubmitted) : ''
      );
      formData.append('import_permit', updateExport.importPermit ? dateToCustomFormat(updateExport.importPermit) : '');
      formData.append('original_permit_no', updateExport.originalPermitNo);
      if (updateExport.permitExpiry) {
        formData.append(
          'permit_expiry',
          updateExport.permitExpiry ? dateToCustomFormat(updateExport.permitExpiry) : ''
        );
      }
      // Only add file_name when a new file is being uploaded
      if (updateExport.file) {
        formData.append('file', updateExport.file);
      }
      // Always include the original file_name so backend can delete the old file if needed
      if (exportToUpdate.file_name) {
        formData.append('file_name', exportToUpdate.file_name);
      }

      const response = await axiosInstance.put(`/trade/exports/${exportToUpdate.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200) {
        toast.success(`Export "${updateExport.product}" updated successfully`);
        handleUpdateExportDialogClose();

        // Refresh the export list to get the updated data from the server
        const refreshData = async () => {
          try {
            setIsLoading(true);
            // Calculate the statuses to ignore based on selected statuses
            const allStatuses = ['Applied For', 'Used', 'Unused', 'Sent to Exporter'];
            const statusesToIgnore = allStatuses.filter(status => !selectedPermitStatuses.includes(status));
            // If all statuses are selected, send [""], otherwise send the filtered array
            const finalIgnoreArray = statusesToIgnore.length === 0 ? [""] : statusesToIgnore;

            const response = await axiosInstance.post(
              `/trade/filter-exports?page_size=${rowsPerPage}&page_number=${page + 1}`,
              {
                search_key: filterValue,
                permit_status: '',
                import_location: selectedImportLocation, // Use the selected import location
                sort_column_name: sortColumnName, // Use the selected sort column
                sort_type: sortType, // Use the selected sort type
                ignore_permit_status: finalIgnoreArray,
              }
            );

            if (response.status === 200) {
              // The new response structure has meta and payload
              const { payload: data, meta } = response.data;
              setTotal(meta.total_size || data.length); // Set total for pagination from meta

              // Add serial numbers to the data
              const dataWithSerial = data.map((item, index) => ({
                ...item,
                sl: index + 1 + page * rowsPerPage, // Calculate serial based on current page
              }));

              setTableData(dataWithSerial);
            }
            setIsLoading(false);
          } catch (error) {
            setIsLoading(false);
            console.error('Error refreshing exports:', error);
          }
        };

        refreshData();
      }
    } catch (error) {
      console.error('Error updating export:', error);
      if (error.response?.data?.message) {
        toast.error(`Failed to update export: ${error.response.data.message}`);
      } else {
        toast.error('Failed to update export. Please try again.');
      }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    // Reset sort when page size changes
    setSortColumnName('');
    setSortType('');
  };

  useEffect(() => {
    return setPage(0);
  }, []);

  // Set default dates when component mounts
  useEffect(() => {
    const today = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(today.getMonth() - 6);

    setStartDate(sixMonthsAgo);
    setEndDate(today);
  }, []);

  // Effect to refetch data when any filter changes including selectedPermitStatuses
  useEffect(() => {
    const getData = async () => {
      try {
        setIsLoading(true);
        // Calculate the statuses to ignore based on selected statuses
        const allStatuses = ['Applied For', 'Used', 'Unused', 'Sent to Exporter'];
        const statusesToIgnore = allStatuses.filter(status => !selectedPermitStatuses.includes(status));
        // If all statuses are selected, send [""], otherwise send the filtered array
        const finalIgnoreArray = statusesToIgnore.length === 0 ? [""] : statusesToIgnore;

        const response = await axiosInstance.post(
          `/trade/filter-exports?page_size=${rowsPerPage}&page_number=${page + 1}`,
          {
            search_key: filterValue,
            permit_status: '',
            import_location: selectedImportLocation, // Use the selected import location
            sort_column_name: sortColumnName, // Use the selected sort column
            sort_type: sortType, // Use the selected sort type
            ignore_permit_status: finalIgnoreArray,
          }
        );

        if (response.status === 200) {
          // The new response structure has meta and payload
          const { payload: data, meta } = response.data;
          setTotal(meta.total_size || data.length); // Set total for pagination from meta

          // Add serial numbers to the data
          const dataWithSerial = data.map((item, index) => ({
            ...item,
            sl: index + 1 + page * rowsPerPage, // Calculate serial based on current page
          }));

          setTableData(dataWithSerial);
        }
      } catch (error) {
        console.error('Error fetching exports:', error);
        setTableData([]);
        if (error.response?.data?.message) {
          toast.error(`Failed to fetch exports: ${error.response.data.message}`);
        } else {
          toast.error('Failed to fetch exports. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    getData();
  }, [rowsPerPage, page, filterValue, selectedImportLocation, sortColumnName, sortType, selectedPermitStatuses]);

  const handleAddExportClick = () => {
    setAddExportDialogOpen(true);
    setNewExport({
      status: 'Applied For',
      product: '',
      qtyApplied: '',
      importLocation: '',
      applicationSubmitted: null,
      importPermit: '',
      originalPermitNo: '',
      file: null,
      permitExpiry: null,
    });
    setNewExportErrors({});
  };

  const handleAddExportDialogClose = () => {
    setAddExportDialogOpen(false);
    setNewExport({
      status: 'Applied For',
      product: '',
      qtyApplied: '',
      importLocation: '',
      applicationSubmitted: null,
      importPermit: '',
      originalPermitNo: '',
      file: null,
      permitExpiry: null,
    });
    setNewExportErrors({});
  };

  const handleAddExport = async () => {
    // Basic validation
    const errors = {};
    if (!newExport.status) errors.status = 'Status is required';
    if (!newExport.product?.trim()) errors.product = 'Product is required';
    if (!newExport.qtyApplied || newExport.qtyApplied <= 0) errors.qtyApplied = 'Quantity must be greater than 0';
    if (!newExport.importLocation) errors.importLocation = 'Import Location is required';
    if (!newExport.applicationSubmitted) errors.applicationSubmitted = 'Application Submitted date is required';
    // if (!newExport.importPermit) errors.importPermit = 'Import Permit is required';
    // if (!newExport.originalPermitNo?.trim()) errors.originalPermitNo = 'Original Permit No. is required';
    // if (!newExport.permitExpiry) errors.permitExpiry = 'Permit Expiry date is required';

    if (Object.keys(errors).length > 0) {
      setNewExportErrors(errors);
      return;
    }

    try {
      // Create FormData for the new export
      const formData = new FormData();
      formData.append('permit_status', newExport.status);
      formData.append('product', newExport.product);
      formData.append('qty_applied', newExport.qtyApplied);
      formData.append('import_location', newExport.importLocation);
      formData.append('application_submitted', dateToCustomFormat(newExport.applicationSubmitted));
      formData.append('import_permit', newExport.importPermit ? dateToCustomFormat(newExport.importPermit) : '');
      formData.append('original_permit_no', newExport.originalPermitNo);
      formData.append('permit_expiry', newExport.permitExpiry ? dateToCustomFormat(newExport.permitExpiry) : '');
      if (newExport.file) {
        formData.append('file', newExport.file);
      }

      const response = await axiosInstance.post('/trade/exports', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 201) {
        toast.success(`Export "${newExport.product}" added successfully`);
        handleAddExportDialogClose();

        // Refresh the export list to get the updated data from the server
        const refreshData = async () => {
          try {
            setIsLoading(true);
            // Calculate the statuses to ignore based on selected statuses
            const allStatuses = ['Applied For', 'Used', 'Unused', 'Sent to Exporter'];
            const statusesToIgnore = allStatuses.filter(status => !selectedPermitStatuses.includes(status));
            // If all statuses are selected, send [""], otherwise send the filtered array
            const finalIgnoreArray = statusesToIgnore.length === 0 ? [""] : statusesToIgnore;

            const response = await axiosInstance.post(
              `/trade/filter-exports?page_size=${rowsPerPage}&page_number=${page + 1}`,
              {
                search_key: filterValue,
                permit_status: '',
                import_location: selectedImportLocation, // Use the selected import location
                sort_column_name: sortColumnName, // Use the selected sort column
                sort_type: sortType, // Use the selected sort type
                ignore_permit_status: finalIgnoreArray,
              }
            );

            if (response.status === 200) {
              // The new response structure has meta and payload
              const { payload: data, meta } = response.data;
              setTotal(meta.total_size || data.length); // Set total for pagination from meta

              // Add serial numbers to the data
              const dataWithSerial = data.map((item, index) => ({
                ...item,
                sl: index + 1 + page * rowsPerPage, // Calculate serial based on current page
              }));

              setTableData(dataWithSerial);
            }
            setIsLoading(false);
          } catch (error) {
            setIsLoading(false);
            console.error('Error refreshing exports:', error);
          }
        };

        refreshData();
      }
    } catch (error) {
      console.error('Error adding export:', error);
      if (error.response?.data?.message) {
        toast.error(`Failed to add export: ${error.response.data.message}`);
      } else {
        toast.error('Failed to add export. Please try again.');
      }
    }
  };

  // Export function to download CSV
  const handleExport = async () => {
    try {
      // Format dates to YYYY-MM-DD format
      const formatDate = (date) => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const formattedStartDate = startDate ? formatDate(startDate) : '';
      const formattedEndDate = endDate ? formatDate(endDate) : '';

      // Calculate the statuses to ignore based on selected statuses
      const allStatuses = ['Applied For', 'Used', 'Unused', 'Sent to Exporter'];
      const statusesToIgnore = allStatuses.filter(status => !selectedPermitStatuses.includes(status));
      // If all statuses are selected, send [""], otherwise send the filtered array
      const finalIgnoreArray = statusesToIgnore.length === 0 ? [""] : statusesToIgnore;

      // Call the API with the same filters plus date filters
      const response = await axiosInstance.post(
        `/trade/filter-exports?page_size=15000&page_number=1`, // High limit to get all records
        {
          search_key: filterValue,
          import_location: selectedImportLocation,
          sort_column_name: sortColumnName,
          sort_type: sortType,
          start_date: formattedStartDate, // Add start date filter
          end_date: formattedEndDate, // Add end date filter
          ignore_permit_status: finalIgnoreArray,
        }
      );

      if (response.status === 200) {
        const { payload: data } = response.data;
        // Add serial numbers to the data
        const dataWithSerial = data.map((item, index) => ({
          ...item,
          sl: index + 1,
        }));
        downloadCSV(dataWithSerial);
        // Close the modal after successful export
        setExportModalOpen(false);
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data. Please try again.');
    }
  };

  const handleAddFieldChange = (field, value) => {
    setAddFieldData({
      ...addFieldData,
      [field]: value,
    });

    // Clear error when user updates the field
    if (addFieldErrors[field]) {
      setAddFieldErrors({
        ...addFieldErrors,
        [field]: null,
      });
    }
  };

  // Function to convert data to CSV and download
  const downloadCSV = (data) => {
    if (!data || data.length === 0) {
      toast.warning('No data to export');
      return;
    }

    // Define the CSV headers based on the data structure
    const headers = [
      'SL',
      'Status',
      'Product',
      'Qty Applied',
      'Import Location',
      'Application Submitted',
      'Date Permit Received',
      'Original Permit No.',
      'Permit Expiry',
      'Actual Quantity',
      'Used Date',
    ];

    // Format the data rows
    const rows = data.map((item) => [
      item.sl || '',
      item.permit_status || '',
      item.product || '',
      item.qty_applied || '',
      item.import_location || '',
      formatDateForDisplay(item.application_submitted) || '',
      formatDateForDisplay(item.import_permit) || '',
      item.original_permit_no || '',
      formatDateForDisplay(item.permit_expiry) || '',
      item.actual_quantity || '',
      item.used_date ? formatDateForDisplay(item.used_date) : '',
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    // Create a Blob with the CSV content
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    // Create a link element to trigger the download
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    // Set the filename with current date
    const date = new Date().toISOString().slice(0, 10);
    link.setAttribute('href', url);
    link.setAttribute('download', `exports_${date}.csv`);
    link.style.visibility = 'hidden';

    // Append, click, and remove the link to trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleAddFieldDialogClose = () => {
    setAddFieldDialogOpen(false);
    setExportToAddField(null);
    setAddFieldData({
      actual_quantity: '',
      used_date: null,
    });
    setAddFieldErrors({});
  };

  const handleAddField = async () => {
    // Basic validation
    const errors = {};
    if (!addFieldData.actual_quantity || addFieldData.actual_quantity <= 0) {
      errors.actual_quantity = 'Actual Quantity must be greater than 0';
    }
    if (!addFieldData.used_date) {
      errors.used_date = 'Used Date is required';
    }

    if (Object.keys(errors).length > 0) {
      setAddFieldErrors(errors);
      return;
    }

    // Format date to yyyy-mm-dd
    const formatDateToYMD = (date) => {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    try {
      const response = await axiosInstance.put('/trade/add-supplier-fields/export', {
        id: exportToAddField.id,
        actual_quantity: parseFloat(addFieldData.actual_quantity),
        used_date: formatDateToYMD(addFieldData.used_date),
      });

      if (response.status === 200) {
        toast.success('Field added successfully');
        handleAddFieldDialogClose();

        // Refresh the export list to get the updated data from the server
        const refreshData = async () => {
          try {
            setIsLoading(true);
            // Calculate the statuses to ignore based on selected statuses
            const allStatuses = ['Applied For', 'Used', 'Unused', 'Sent to Exporter'];
            const statusesToIgnore = allStatuses.filter(status => !selectedPermitStatuses.includes(status));
            // If all statuses are selected, send [""], otherwise send the filtered array
            const finalIgnoreArray = statusesToIgnore.length === 0 ? [""] : statusesToIgnore;

            const response = await axiosInstance.post(
              `/trade/filter-exports?page_size=${rowsPerPage}&page_number=${page + 1}`,
              {
                search_key: filterValue,
                permit_status: '',
                import_location: selectedImportLocation, // Use the selected import location
                sort_column_name: sortColumnName, // Use the selected sort column
                sort_type: sortType, // Use the selected sort type
                ignore_permit_status: finalIgnoreArray,
              }
            );

            if (response.status === 200) {
              // The new response structure has meta and payload
              const { payload: data, meta } = response.data;
              setTotal(meta.total_size || data.length); // Set total for pagination from meta

              // Add serial numbers to the data
              const dataWithSerial = data.map((item, index) => ({
                ...item,
                sl: index + 1 + page * rowsPerPage, // Calculate serial based on current page
              }));

              setTableData(dataWithSerial);
            }
            setIsLoading(false);
          } catch (error) {
            setIsLoading(false);
            console.error('Error refreshing exports:', error);
          }
        };

        refreshData();
      }
    } catch (error) {
      console.error('Error adding field:', error);
      if (error.response?.data?.message) {
        toast.error(`Failed to add field: ${error.response.data.message}`);
      } else {
        toast.error('Failed to add field. Please try again.');
      }
    }
  };

  const handleInputChange = (field, value) => {
    // Special validation for product field to ensure only valid options are accepted
    if (field === 'product') {
      // Check if the value exists in the options list
      const isValidOption = productOptions.includes(value);
      if (isValidOption || value === '') {
        // Only update if it's a valid option or empty
        setNewExport({
          ...newExport,
          [field]: value,
        });
      } else {
        // If not valid, don't update the main state but keep the temp input value
        // This will cause the field to revert to the last valid value
      }
    } else {
      // For other fields, update normally
      setNewExport({
        ...newExport,
        [field]: value,
      });
    }

    // Clear error when user updates the field
    if (newExportErrors[field]) {
      setNewExportErrors({
        ...newExportErrors,
        [field]: null,
      });
    }
  };

  // Status options
  const statusOptions = [
    { value: 'Applied For', label: 'Applied For' },
    { value: 'Used', label: 'Used' },
    { value: 'Unused', label: 'Unused' },
    { value: 'Sent to Exporter', label: 'Sent to Exporter' },
  ];

  return (
    <Page title="Manage Exports">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} ml={2}>
          <Typography variant="h4" gutterBottom>
            Manage Exports
          </Typography>
          <Button variant="contained" sx={{ mr: 2 }} onClick={handleAddExportClick}>
            Add New Export
          </Button>
        </Stack>

        <Card sx={{ pt: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="flex-end" mb={2} ml={2}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mr: 2 }}>
              {(searchValue || selectedImportLocation) && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setSearchValue('');
                    setFilterValue('');
                    setSelectedImportLocation('');
                  }}
                >
                  Clear Filters
                </Button>
              )}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setFilterValue(searchValue);
                  setPage(0); // Reset to first page when searching
                }}
              >
                <OutlinedInput
                  size="small"
                  placeholder="Search..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <button type="submit" style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                        <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
                      </button>
                    </InputAdornment>
                  }
                  sx={{ minWidth: 200 }}
                />
              </form>
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel id="import-location-label">Import Location</InputLabel>
                <Select
                  labelId="import-location-label"
                  id="importLocation"
                  value={selectedImportLocation}
                  label="Import Location"
                  onChange={(e) => {
                    setSelectedImportLocation(e.target.value);
                    setPage(0); // Reset to first page when changing filter
                  }}
                >
                  {importLocationsLoading ? (
                    <MenuItem value="" disabled>
                      <CircularProgress size={20} />
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        Loading...
                      </Typography>
                    </MenuItem>
                  ) : (
                    importLocations.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 250 }} size="small">
                <InputLabel id="permit-statuses-label">Permit Statuses</InputLabel>
                <Select
                  labelId="permit-statuses-label"
                  id="permitStatuses"
                  multiple
                  value={selectedPermitStatuses}
                  onChange={(e) => {
                    const value = e.target.value;
                    // If all options are selected, use the array as is
                    // If it's a function (in case of special selections), handle appropriately
                    const newValue = typeof value === 'function' ? value(selectedPermitStatuses) : value;
                    setSelectedPermitStatuses(newValue);
                    setPage(0); // Reset to first page when changing filter

                    // Refresh the export list to get the updated data from the server
                    const refreshData = async () => {
                      try {
                        setIsLoading(true);
                        // Calculate the statuses to ignore based on selected statuses
                        const allStatuses = ['Applied For', 'Used', 'Unused', 'Sent to Exporter'];
                        const statusesToIgnore = allStatuses.filter(status => !newValue.includes(status));
                        // If all statuses are selected, send [""], otherwise send the filtered array
                        const finalIgnoreArray = statusesToIgnore.length === 0 ? [""] : statusesToIgnore;

                        const response = await axiosInstance.post(
                          `/trade/filter-exports?page_size=${rowsPerPage}&page_number=1`, // Always fetch page 1 when filter changes
                          {
                            search_key: filterValue,
                            permit_status: '',
                            import_location: selectedImportLocation, // Use the selected import location
                            sort_column_name: sortColumnName, // Use the selected sort column
                            sort_type: sortType, // Use the selected sort type
                            ignore_permit_status: finalIgnoreArray,
                          }
                        );

                        if (response.status === 200) {
                          // The new response structure has meta and payload
                          const { payload: data, meta } = response.data;
                          setTotal(meta.total_size || data.length); // Set total for pagination from meta

                          // Add serial numbers to the data
                          const dataWithSerial = data.map((item, index) => ({
                            ...item,
                            sl: index + 1, // Calculate serial starting from 1 for page 1
                          }));

                          setTableData(dataWithSerial);
                        }
                        setIsLoading(false);
                      } catch (error) {
                        setIsLoading(false);
                        console.error('Error refreshing exports:', error);
                      }
                    };

                    refreshData();
                  }}
                  renderValue={(selected) => selected.join(', ')}
                  label="Permit Statuses"
                >
                  {['Applied For', 'Used', 'Unused', 'Sent to Exporter'].map((status) => (
                    <MenuItem key={status} value={status}>
                      <Checkbox checked={selectedPermitStatuses.indexOf(status) > -1} />
                      <ListItemText primary={status} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:download-outline" />}
                onClick={() => setExportModalOpen(true)}
                sx={{ ml: 2 }}
              >
                Export
              </Button>
            </Stack>
          </Stack>

          {/* Export Modal */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Dialog open={exportModalOpen} onClose={() => setExportModalOpen(false)}>
              <DialogTitle>Export Data</DialogTitle>
              <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>Select date range for export:</DialogContentText>
                <Stack spacing={2} sx={{ minWidth: 300, mt: 1 }}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(newValue) => {
                      setStartDate(newValue);
                    }}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(newValue) => {
                      setEndDate(newValue);
                    }}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setExportModalOpen(false)}>Cancel</Button>
                <Button variant="contained" onClick={handleExport} startIcon={<Iconify icon="eva:download-outline" />}>
                  Export
                </Button>
              </DialogActions>
            </Dialog>
          </LocalizationProvider>
          <Scrollbar>
            <div style={{ height: 520, width: '100%', p: 3 }} className="ag-theme-material">
              {isLoading ? (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <CircularProgress size={72} sx={{ mb: 3 }} />
                  <h5>Please wait while we prepare your data</h5>
                </div>
              ) : (
                <ModernAgGrid
                  rowData={tableData}
                  columnDefs={columnDefs}
                  gridRef={gridRef}
                  style={{ height: 520, minWidth: 750 }}
                />
              )}
            </div>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[10, 15, 25, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>

        {/* Add Export Dialog */}
        <Dialog open={addExportDialogOpen} onClose={handleAddExportDialogClose} maxWidth="md" fullWidth>
          <DialogTitle>Add New Export</DialogTitle>
          <DialogContent dividers>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Autocomplete
                    id="product"
                    options={productOptions}
                    value={newExport.product}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        handleInputChange('product', newValue);
                        setNewExportTempInput(newValue);
                        // Clear error when user updates the field
                        if (newExportErrors.product) {
                          setNewExportErrors({
                            ...newExportErrors,
                            product: null,
                          });
                        }
                      }
                    }}
                    filterOptions={(options, state) => {
                      if (state.inputValue === '') {
                        return options;
                      }
                      return options.filter((option) => option.toLowerCase().includes(state.inputValue.toLowerCase()));
                    }}
                    onInputChange={(event, newInputValue) => {
                      // Update temporary input for search
                      setNewExportTempInput(newInputValue);
                      // Only search when user types
                      debouncedProductSearch(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Product"
                        error={!!newExportErrors.product}
                        helperText={newExportErrors.product}
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth error={!!newExportErrors.status} size="small">
                    <InputLabel id="status-label">Status</InputLabel>
                    <Select
                      labelId="status-label"
                      id="status"
                      value={newExport.status}
                      label="Status"
                      onChange={(e) => handleInputChange('status', e.target.value)}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {newExportErrors.status && (
                      <Typography color="error" variant="caption">
                        {newExportErrors.status}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth error={!!newExportErrors.importLocation} size="small">
                    <InputLabel id="import-location-label">Import Location</InputLabel>
                    <Select
                      labelId="import-location-label"
                      id="importLocation"
                      value={newExport.importLocation}
                      label="Import Location"
                      onChange={(e) => handleInputChange('importLocation', e.target.value)}
                    >
                      {importLocationsLoading ? (
                        <MenuItem value="" disabled>
                          <CircularProgress size={20} />
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            Loading...
                          </Typography>
                        </MenuItem>
                      ) : (
                        importLocations.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {newExportErrors.importLocation && (
                      <Typography color="error" variant="caption">
                        {newExportErrors.importLocation}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="originalPermitNo"
                    label="Original Permit No."
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={newExport.originalPermitNo}
                    onChange={(e) => handleInputChange('originalPermitNo', e.target.value)}
                    // error={!!newExportErrors.originalPermitNo}
                    // helperText={newExportErrors.originalPermitNo}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="file"
                    type="file"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      type: 'file',
                      inputProps: {
                        accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png',
                      },
                    }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Check file size (100MB = 100 * 1024 * 1024 bytes)
                        const maxSize = 100 * 1024 * 1024; // 100MB in bytes
                        if (file.size > maxSize) {
                          toast.error('File size exceeds 100MB limit');
                          // Clear the input field
                          e.target.value = '';
                          return;
                        }
                      }
                      handleInputChange('file', file);
                    }}
                    size="small"
                    helperText="Max file size: 100MB"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="qtyApplied"
                    label="Qty Applied"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={newExport.qtyApplied}
                    onChange={(e) => handleInputChange('qtyApplied', e.target.value)}
                    error={!!newExportErrors.qtyApplied}
                    helperText={newExportErrors.qtyApplied}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="Import Permit"
                    value={newExport.importPermit}
                    onChange={(newValue) => handleInputChange('importPermit', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={false}
                        // error={!!newExportErrors.importPermit}
                        // helperText={newExportErrors.importPermit}
                        size="small"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6}>
                  <DatePicker
                    label="Application Submitted"
                    value={newExport.applicationSubmitted}
                    onChange={(newValue) => handleInputChange('applicationSubmitted', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={!!newExportErrors.applicationSubmitted}
                        helperText={newExportErrors.applicationSubmitted}
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="Permit Expiry"
                    value={newExport.permitExpiry}
                    onChange={(newValue) => handleInputChange('permitExpiry', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        // error={!!newExportErrors.permitExpiry}
                        // helperText={newExportErrors.permitExpiry}
                        size="small"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddExportDialogClose}>Cancel</Button>
            <Button onClick={handleAddExport} variant="contained">
              Add
            </Button>
          </DialogActions>
        </Dialog>

        {/* 
        <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Are you sure you want to delete this export?
            </Alert>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Product:</strong> {exportToDelete?.product}
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Status:</strong> {exportToDelete?.permit_status}
            </Typography>
            <Typography variant="subtitle1">
              <strong>Import Location:</strong> {exportToDelete?.import_location}
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteDialogClose}>Cancel</Button>
            <Button onClick={handleDeleteExport} variant="contained" color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        */}

        {/* Update Export Dialog */}
        <Dialog open={updateExportDialogOpen} onClose={handleUpdateExportDialogClose} maxWidth="md" fullWidth>
          <DialogTitle>Update Export</DialogTitle>
          <DialogContent dividers>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Autocomplete
                    id="update-product"
                    options={productOptions}
                    value={updateExport.product}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        handleUpdateExportChange('product', newValue);
                        setUpdateExportTempInput(newValue);
                        // Clear error when user updates the field
                        if (updateExportErrors.product) {
                          setUpdateExportErrors({
                            ...updateExportErrors,
                            product: null,
                          });
                        }
                      }
                    }}
                    filterOptions={(options, state) => {
                      if (state.inputValue === '') {
                        return options;
                      }
                      return options.filter((option) => option.toLowerCase().includes(state.inputValue.toLowerCase()));
                    }}
                    onInputChange={(event, newInputValue) => {
                      // Update temporary input for search
                      setUpdateExportTempInput(newInputValue);
                      // Only search when user types
                      debouncedProductSearch(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Product"
                        error={!!updateExportErrors.product}
                        helperText={updateExportErrors.product}
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth error={!!updateExportErrors.status} size="small">
                    <InputLabel id="update-status-label">Status</InputLabel>
                    <Select
                      labelId="update-status-label"
                      id="update-status"
                      value={updateExport.status}
                      label="Status"
                      onChange={(e) => handleUpdateExportChange('status', e.target.value)}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {updateExportErrors.status && (
                      <Typography color="error" variant="caption">
                        {updateExportErrors.status}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth error={!!updateExportErrors.importLocation} size="small">
                    <InputLabel id="update-import-location-label">Import Location</InputLabel>
                    <Select
                      labelId="update-import-location-label"
                      id="update-importLocation"
                      value={updateExport.importLocation}
                      label="Import Location"
                      onChange={(e) => handleUpdateExportChange('importLocation', e.target.value)}
                    >
                      {importLocationsLoading ? (
                        <MenuItem value="" disabled>
                          <CircularProgress size={20} />
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            Loading...
                          </Typography>
                        </MenuItem>
                      ) : (
                        importLocations.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {updateExportErrors.importLocation && (
                      <Typography color="error" variant="caption">
                        {updateExportErrors.importLocation}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="update-originalPermitNo"
                    label="Original Permit No."
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={updateExport.originalPermitNo}
                    onChange={(e) => handleUpdateExportChange('originalPermitNo', e.target.value)}
                    // error={!!updateExportErrors.originalPermitNo}
                    // helperText={updateExportErrors.originalPermitNo}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="update-file"
                    type="file"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      type: 'file',
                      inputProps: {
                        accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png',
                      },
                    }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Check file size (100MB = 100 * 1024 * 1024 bytes)
                        const maxSize = 100 * 1024 * 1024; // 100MB in bytes
                        if (file.size > maxSize) {
                          toast.error('File size exceeds 100MB limit');
                          // Clear the input field
                          e.target.value = '';
                          return;
                        }
                      }
                      handleUpdateExportChange('file', file);
                    }}
                    size="small"
                    helperText="Max file size: 100MB"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="update-qtyApplied"
                    label="Qty Applied"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={updateExport.qtyApplied}
                    onChange={(e) => handleUpdateExportChange('qtyApplied', e.target.value)}
                    error={!!updateExportErrors.qtyApplied}
                    helperText={updateExportErrors.qtyApplied}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="Import Permit"
                    value={updateExport.importPermit}
                    onChange={(newValue) => handleUpdateExportChange('importPermit', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={false}
                        // error={!!updateExportErrors.importPermit}
                        // helperText={updateExportErrors.importPermit}
                        size="small"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6}>
                  <DatePicker
                    label="Application Submitted"
                    value={updateExport.applicationSubmitted}
                    onChange={(newValue) => handleUpdateExportChange('applicationSubmitted', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={!!updateExportErrors.applicationSubmitted}
                        helperText={updateExportErrors.applicationSubmitted}
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="Permit Expiry"
                    value={updateExport.permitExpiry}
                    onChange={(newValue) => handleUpdateExportChange('permitExpiry', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={false}
                        // error={!!updateExportErrors.permitExpiry}
                        // helperText={updateExportErrors.permitExpiry}
                        size="small"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleUpdateExportDialogClose}>Cancel</Button>
            <Button onClick={handleUpdateExport} variant="contained">
              Update
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Field Dialog */}
        <Dialog open={addFieldDialogOpen} onClose={handleAddFieldDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Field</DialogTitle>
          <DialogContent dividers>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    id="actual_quantity"
                    label="Actual Quantity Number"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={addFieldData.actual_quantity}
                    onChange={(e) => handleAddFieldChange('actual_quantity', e.target.value)}
                    error={!!addFieldErrors.actual_quantity}
                    helperText={addFieldErrors.actual_quantity}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <DatePicker
                    label="Used Date"
                    value={addFieldData.used_date}
                    onChange={(newValue) => handleAddFieldChange('used_date', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={!!addFieldErrors.used_date}
                        helperText={addFieldErrors.used_date}
                        size="small"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddFieldDialogClose}>Cancel</Button>
            <Button onClick={handleAddField} variant="contained">
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
};

export default ManageExports;
